<template>
  <ConfirmationDialog v-bind="$props" ref="confirmationDialog" :buttonText="$t('actions.delete')" @execute="onConfirm">
    <template slot="content">
      <div class="confirmation-dialog__content">
        <i18n path="page.opportunity.delete-document-confirmation">
          <template v-slot:action>
            <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
          </template>
          <template v-slot:documentName v-if="manipulatedObject">
            <span class="confirmation-dialog__document"> {{ manipulatedObject.name }}</span>
          </template>
        </i18n>
        <div class="dialog__warning-notice">
          {{ $t('page.opportunity.delete-document-warning') }}
        </div>
        <div v-if="loading || hasImpact" class="document-delete-impact">
          <div class="document-delete-impact__alert-icon">
            <BaseIcon name="alert-circle" />
          </div>
          <div class="document-delete-impact__text">
            <BaseCircularLoadingBar v-if="loading" :size="14" :width="2" color="accent" />
            <template v-else>
              <span>{{ $t('page.opportunity.delete-document-impact-title') }}</span>
              <span v-if="taskCount > 0">{{
                $tc('page.opportunity.delete-document-impact-tasks', taskCount, { count: taskCount })
              }}</span>
              <span v-if="taskCitationCount > 0">{{
                $tc('page.opportunity.delete-document-impact-citations', taskCitationCount, {
                  count: taskCitationCount,
                })
              }}</span>
            </template>
          </div>
        </div>
      </div>
    </template>
  </ConfirmationDialog>
</template>

<script>
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import { opportunityDocumentsService } from '@/services/opportunity-documents.service';

export default {
  name: 'DocumentDeleteConfirmationPopup',
  components: { ConfirmationDialog, BaseIcon, BaseCircularLoadingBar },
  props: {
    manipulatedObject: {
      type: Object,
      required: false,
    },
    opportunityId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      impact: null,
    };
  },
  computed: {
    taskCount() {
      return this.impact ? this.impact.taskCount : 0;
    },
    taskCitationCount() {
      return this.impact ? this.impact.taskCitationCount : 0;
    },
    hasImpact() {
      return this.taskCount > 0 || this.taskCitationCount > 0;
    },
  },
  methods: {
    openDialog(document) {
      this.impact = null;
      this.$refs.confirmationDialog.openDialog();
      this.fetchImpact(document);
    },
    closeDialog() {
      this.$refs.confirmationDialog.closeDialog();
    },
    fetchImpact(document) {
      if (!document || !this.opportunityId) {
        return;
      }
      this.loading = true;
      return opportunityDocumentsService
        .getDeletionImpact({ opportunityId: this.opportunityId, documentId: document.id })
        .then((impact) => {
          this.impact = impact;
        })
        .catch(() => {
          this.impact = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onConfirm() {
      this.$emit('on-confirm', this.manipulatedObject);
    },
  },
  emits: ['on-confirm'],
};
</script>

<style scoped lang="scss">
.document-delete-impact {
  margin-top: 16px;
  padding: 8px;
  border-radius: 5px;
  background: $color-semantic-error-light;
  display: flex;
  align-items: center;
  gap: 16px;

  &__alert-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: $color-semantic-error;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 16px;

    span:first-child {
      font-weight: $font-weight-bold;
    }
  }
}
</style>
